import React from 'react'
import { Row } from 'react-bootstrap'
import { components } from 'react-select'
import type { MenuListProps, OptionProps } from 'react-select'
import type { ContactSelectProps } from '@ztechapps/webui.contract'
import type { SelectOption } from '@ztechapps/webui.input'
import { UserTypeEnum } from '@ztechapps/common.contract'
import { buildUserSelectLabel } from '@ztechapps/common.utils'
import { SelectAsync, SelectOptionColumn } from '@ztechapps/webui.input'
import { useContactSelect } from '@ztechapps/customer.common'
import { DisplayName, IconValueBlock, StatusBadge } from '@ztechapps/webui.util'

// The Type column is narrow and fixed so the header and every row line up.
const typeColumnClass = 'col-2'

const ContactMenuList = ({ children, ...rest }: MenuListProps<SelectOption>) => (
  <components.MenuList {...rest} className="m-0 p-0">
    <Row className="m-0 bg-secondary-subtle fw-bold sticky-top">
      <SelectOptionColumn>Company Name</SelectOptionColumn>
      <SelectOptionColumn>Contact Person</SelectOptionColumn>
      <SelectOptionColumn>Email</SelectOptionColumn>
      <SelectOptionColumn className={typeColumnClass}>Type</SelectOptionColumn>
    </Row>
    {children}
  </components.MenuList>
)

const ContactOption = ({ data, ...rest }: OptionProps<SelectOption>) => {
  const contactName = data?.profile?.name ?? ''

  // Vendor rows take the contact header's vendor blue; customer rows its customer green.
  const typeColor = data?.type === UserTypeEnum.vendor ? 'var(--bs-info)' : 'var(--bs-success)'

  // `isSelected` stays false so no row highlights.
  return (
    <components.Option {...rest} data={data} isSelected={false} className="m-0 p-0">
      <Row className="m-0">
        <SelectOptionColumn>
          <div className="fw-bold">
            <DisplayName company={data?.company} profile={data?.profile} />
          </div>
        </SelectOptionColumn>
        <SelectOptionColumn className="small">
          <span title={contactName}>{contactName}</span>
        </SelectOptionColumn>
        <SelectOptionColumn className="small">
          <IconValueBlock icon="envelope">{data?.profile?.email}</IconValueBlock>
        </SelectOptionColumn>
        <SelectOptionColumn className={typeColumnClass}>
          <StatusBadge status={data?.type} statusColor={typeColor} />
        </SelectOptionColumn>
      </Row>
    </components.Option>
  )
}

export const ContactSelect = ({ filter, ...rest }: ContactSelectProps) => {
  const { dataLoadOptions } = useContactSelect()

  return (
    <SelectAsync
      placeholder="Type company or email or name."
      noOptionsMessage={() => 'Cannot find any contacts.'}
      {...rest}
      dataLoadOptions={dataLoadOptions({ filter })}
      components={{ Option: ContactOption, MenuList: ContactMenuList }}
      getOptionLabel={buildUserSelectLabel}
    />
  )
}
